import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { SelectField } from '@/components/forms/inputs/SelectField/SelectField';
import { accountFormModel } from '@/components/forms/forms/account/accountFormModel';
import { SaveButtonWithCompletionStatus } from '@/components/general/buttons/SaveButton/SaveButtonWithCompletionStatus';
import { PHYSICIAN_PROFESSION_GROUP, PROFESSIONS } from '@/consts/forms/registration';
import { ACCOUNT_GENERAL_PHRASES } from '@/consts/phrases/accountPhrases';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { useCompletionStatus } from '@/hooks/general/useCompletionStatus';
import { useProfessionalDetails } from '@/hooks/forms/register/useProfessionalDetails';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { accountService } from '@/services/api/accountService/accountService';
import { PATIENT_CONSUMER_PROFESSION_ID } from '@/types/const/index';
import { AccountTabs } from '@/types/enums/forms/account/accountTabs';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IAccountProfessionalDetails,
    IAllAccountData,
} from '@/types/types/forms/account/account.interface';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

interface AccountProfessionalDetailsFormProps {
    accountData?: IAllAccountData;
    dictionary: DefaultPageProps['dictionary'];
}

export const AccountProfessionalDetailsForm: FC<AccountProfessionalDetailsFormProps> = ({
    accountData,
    dictionary,
}: AccountProfessionalDetailsFormProps) => {
    const router = useRouter();

    useEffect(() => {
        if (accountData?.professionId === PATIENT_CONSUMER_PROFESSION_ID) {
            router.push('/');
        }
    }, [accountData, router]);

    const { getCompletionStatus, resetCompletionStatus, completionStatus } = useCompletionStatus();

    const {
        professionId,
        specialtyId,
        graduationYear,
        medicalSchool,
        primaryWorkplaceName,
        workplaceMainlineNumber,
    } = accountFormModel[AccountTabs.ProfessionalDetails];

    const { control, handleSubmit, formState, getValues, watch, setError, clearErrors } = useForm<
        Partial<IAccountProfessionalDetails>
    >({
        values: {
            professionId: accountData?.professionId,
            specialtyId: accountData?.specialtyId,
            medicalSchool: accountData?.medicalSchool ?? '',
            graduationYear: accountData?.graduationYear,
            primaryWorkplaceName: accountData?.primaryWorkplaceName,
            workplaceMainlineNumber: accountData?.workplaceMainlineNumber,
        },
    });

    const { handleServerErrors, serverErrors, clearAllErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const { specialtiesList, yearsList, professionsList } = useProfessionalDetails({
        dictionary,
        accountData,
    });

    const { errors, isSubmitting } = formState;

    const watchProfessionId = Number(watch('professionId'));

    const hasProfession = (professionsToCheck: number | number[]): boolean => {
        if (Array.isArray(professionsToCheck)) {
            return professionsToCheck.includes(watchProfessionId);
        } else {
            return professionsToCheck === watchProfessionId;
        }
    };

    const isPhysicianOrPA = hasProfession([
        ...PHYSICIAN_PROFESSION_GROUP,
        PROFESSIONS.PHYSICIAN_ASSISTANT,
    ]);
    const isPhysician = hasProfession(PHYSICIAN_PROFESSION_GROUP);
    const isNotPatientConsumer = !hasProfession(PROFESSIONS.PATIENT_CONSUMER);

    const submitData = async (values: Partial<IAccountProfessionalDetails>) => {
        const professionId = values?.professionId ? +values.professionId : null;
        const specialtyId = isPhysicianOrPA && values?.specialtyId ? +values.specialtyId : null;
        const medicalSchool = isPhysician && values?.medicalSchool ? values.medicalSchool : null;
        const graduationYear =
            isPhysician && values?.graduationYear ? +values.graduationYear : null;
        const primaryWorkplaceName =
            isNotPatientConsumer && values?.primaryWorkplaceName
                ? values.primaryWorkplaceName
                : null;
        const workplaceMainlineNumber =
            isNotPatientConsumer && values?.workplaceMainlineNumber
                ? values.workplaceMainlineNumber
                : null;

        try {
            await getCompletionStatus(() =>
                accountService.saveProfessionalDetails({
                    professionId,
                    specialtyId,
                    medicalSchool,
                    graduationYear,
                    primaryWorkplaceName,
                    workplaceMainlineNumber,
                }),
            );
            clearAllErrors();
        } catch (err) {
            handleServerErrors(err);
        }
    };

    return (
        <form onSubmit={handleSubmit(submitData)} noValidate>
            <Grid container spacing={2} sx={{ pt: 2 }}>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ mb: 2 }}
                    generalErrorId="message_box"
                />
                <Grid size={{ xs: 12 }}>
                    <SelectField
                        control={control}
                        name={professionId.name}
                        label={professionId.label}
                        data={professionsList}
                        fullWidth
                        error={errors.professionId}
                        data-testid="profession-input"
                    />
                </Grid>
                {isPhysicianOrPA && (
                    <Grid size={12}>
                        <SelectField
                            control={control}
                            name={specialtyId.name}
                            label={specialtyId.label}
                            data={specialtiesList}
                            error={errors.specialtyId}
                            fullWidth
                            data-testid="selectYourSpeciality-input"
                        />
                    </Grid>
                )}
                {isPhysician && (
                    <>
                        <Grid size={12}>
                            <SelectField
                                control={control}
                                name={graduationYear.name}
                                label={graduationYear.label}
                                data={yearsList}
                                fullWidth
                                error={errors.graduationYear}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={medicalSchool.name}
                                label={medicalSchool.label}
                                type="text"
                                role="textbox"
                                fullWidth
                                error={errors.medicalSchool}
                                data-testid="medicalSchoolWentTo-input"
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 100,
                                    },
                                }}
                            />
                        </Grid>
                    </>
                )}
                {isNotPatientConsumer && (
                    <>
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={primaryWorkplaceName.name}
                                label={primaryWorkplaceName.label}
                                type="text"
                                role="textbox"
                                fullWidth
                                error={errors.primaryWorkplaceName}
                                data-testid="primaryWorkplaceName-input"
                                required={!hasProfession([...PHYSICIAN_PROFESSION_GROUP])}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                control={control}
                                name={workplaceMainlineNumber.name}
                                label={workplaceMainlineNumber.label}
                                type="text"
                                role="textbox"
                                fullWidth
                                error={errors.workplaceMainlineNumber}
                                data-testid="workplaceMainlineNumber-input"
                            />
                        </Grid>
                    </>
                )}
            </Grid>
            <Grid
                container
                sx={{
                    justifyContent: 'flex-end',
                    marginTop: 2,
                }}
            >
                <SaveButtonWithCompletionStatus
                    id="submit-professional-details"
                    label={ACCOUNT_GENERAL_PHRASES.SAVE_BUTTON}
                    isSubmitting={isSubmitting}
                    status={completionStatus}
                    resetStatus={resetCompletionStatus}
                />
            </Grid>
        </form>
    );
};
